import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorState from '../components/shared/ErrorState';
import { formatDateTime } from '../utils/formatters';

const ACTIVITY_TYPES = [
  { code: 'ALL', label: 'All' },
  { code: 'STATUS_CHANGE', label: 'Status Changes' },
  { code: 'OVERRIDE_DISMISSED', label: 'Overrides' },
  { code: 'STATEMENT_UPLOAD', label: 'Statements' },
  { code: 'MANUAL_MATCH', label: 'Matches' },
];

function activityColor(type) {
  if (type.includes('STATUS')) return '#F59E0B';
  if (type.includes('OVERRIDE')) return '#6B7280';
  if (type.includes('STATEMENT')) return '#3B82F6';
  if (type.includes('MATCH')) return '#22C55E';
  if (type.includes('RECONCILED')) return '#22C55E';
  return '#9CA3AF';
}

function activityIcon(type) {
  if (type.includes('STATUS')) return '\u21A9';
  if (type.includes('OVERRIDE')) return '\u2717';
  if (type.includes('STATEMENT')) return '\uD83D\uDCC4';
  if (type.includes('MATCH')) return '\u2713';
  if (type.includes('QB')) return '\uD83D\uDCB0';
  return '\u2022';
}

export default function AuditTrailPage() {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('ALL');

  const fetchTrail = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getAuditTrail(100);
      setActions(data.actions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrail();
  }, [fetchTrail]);

  if (loading) return <LoadingSpinner text="Loading audit trail..." />;
  if (error) return <ErrorState message={error} onRetry={fetchTrail} />;

  const filtered = typeFilter === 'ALL'
    ? actions
    : actions.filter((a) => a.action_type?.includes(typeFilter) || a.action_type === typeFilter);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-gray-800 mb-1">Audit Trail</h1>
        <p className="text-sm text-gray-400">Full activity history across all modules</p>
      </div>

      <div className="flex gap-2 mb-6">
        {ACTIVITY_TYPES.map((t) => (
          <button
            key={t.code}
            onClick={() => setTypeFilter(t.code)}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              typeFilter === t.code
                ? 'bg-gray-800 text-white'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-3xl mb-3">&#128203;</div>
          <div className="font-semibold">No activity recorded yet</div>
        </div>
      ) : (
        <div className="relative pl-7">
          {/* Timeline line */}
          <div className="absolute left-[10px] top-2 bottom-2 w-0.5 bg-gray-200" />

          {filtered.map((a) => (
            <div key={a.id} className="relative mb-4">
              {/* Dot */}
              <div
                className="absolute -left-[17px] top-1.5 w-4 h-4 rounded-full flex items-center justify-center text-[9px] text-white font-bold"
                style={{ background: activityColor(a.action_type), boxShadow: '0 0 0 3px #F5F7FA' }}
              >
                {activityIcon(a.action_type)}
              </div>

              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex justify-between items-start mb-1">
                  <div>
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                      {a.action_type?.replace(/_/g, ' ')}
                    </span>
                    {a.module && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">
                        {a.module}
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] text-gray-400 whitespace-nowrap ml-3">
                    {formatDateTime(a.timestamp)}
                  </span>
                </div>

                {a.notes && (
                  <div className="text-sm text-gray-700 mb-1">{a.notes}</div>
                )}

                <div className="text-xs text-gray-400">
                  {a.user && <>by {a.user}</>}
                  {a.vendor_name && <> &middot; {a.vendor_name}</>}
                  {a.entity_type && a.entity_id && (
                    <> &middot; {a.entity_type} #{a.entity_id}</>
                  )}
                  {a.old_value && a.new_value && (
                    <> &middot; {a.old_value} &rarr; {a.new_value}</>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
